import { useEffect, useState, type ReactNode } from 'react';
import { Filter, X } from 'lucide-react';
import { useIsMobile } from '../../hooks/useViewport';
import styles from './FilterSheet.module.css';

interface FilterSheetProps {
  triggerLabel?: string;
  /** Count shown in the pill next to the label. If 0/undefined, hidden. */
  activeCount?: number;
  children: ReactNode;
}

/**
 * FilterSheet — a "Filters" button that opens the controls in a dialog.
 *
 * DESKTOP USED TO RENDER THE CONTROLS INLINE, and on Reports that meant
 * a permanent right-hand column holding saved groups, a scrolling
 * location list, three geography selects, location type and residential
 * monetization. It pushed the report itself into the left third of the
 * screen and was there whether or not anyone was filtering.
 *
 * Both widths now get the button. The presentation differs because the
 * gesture does: a bottom sheet on mobile, a centred dialog on desktop.
 * Everything else — the trigger, the active-count pill, Esc to close,
 * Apply — is shared, so the two cannot drift.
 *
 * Use when a page has 3+ filter controls. For 1-2 just leave them inline.
 */
export function FilterSheet({
  triggerLabel = 'Filters', activeCount, children,
}: FilterSheetProps) {
  const isMobile = useIsMobile();
  const [open, setOpen] = useState(false);

  // Esc closes — mirror the admin Modal's affordance.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Filter size={14} />
        {triggerLabel}
        {!!activeCount && activeCount > 0 && (
          <span className={styles.triggerCount}>{activeCount}</span>
        )}
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Filters"
          className={`${styles.backdrop} ${isMobile ? '' : styles.backdropCentered}`}
          onClick={() => setOpen(false)}
        >
          <div
            className={`${styles.sheet} ${isMobile ? '' : styles.dialog}`}
            onClick={(e) => e.stopPropagation()}
          >
            <header className={styles.sheetHeader}>
              <h2 className={styles.sheetTitle}>Filters</h2>
              <button
                type="button"
                className={styles.sheetClose}
                aria-label="Close filters"
                onClick={() => setOpen(false)}
              >
                <X size={16} />
              </button>
            </header>
            <div className={styles.sheetBody}>{children}</div>
            <button
              type="button"
              className={styles.sheetApply}
              onClick={() => setOpen(false)}
            >
              Apply
            </button>
          </div>
        </div>
      )}
    </>
  );
}
